import { datadogLogs } from '@datadog/browser-logs';
import { SavedCart } from 'services/savedCartApi';
import { Unwrappable, errorStatus, isTransientFailure } from './loadSavedCart';

const SAVE_DEBOUNCE_MS = 1000;

type SaveOutcome = 'saved' | 'retry' | 'failed';

// Order matters: carts restore in the order the donor built them.
const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export interface CartSaverDeps {
  clientId: number;
  refCode?: string;
  createCart: (args: {
    client_id: number;
    recipient_ids: string[];
    ref_code?: string;
    jwt?: string;
    keepalive: boolean;
  }) => Unwrappable<SavedCart>;
  updateCart: (args: {
    id: string;
    recipient_ids: string[];
    jwt?: string;
    keepalive: boolean;
  }) => Unwrappable<SavedCart>;
  getJwt: () => Promise<string | undefined>;
  onSaved: (cart: SavedCart, ids: string[]) => void;
}

/**
 * Saves the donor's selection: the first add at once, later changes debounced, one request in
 * flight at a time.
 */
export class CartSaver {
  private cartId?: string;
  // The selection the server holds.
  private savedIds: string[];
  private latestIds: string[];
  private inFlight?: Promise<void>;
  private timer?: ReturnType<typeof setTimeout>;
  // For a flush that can't wait for a fresh JWT.
  private lastJwt?: string;
  private drainAfterInFlight = false;

  constructor(
    private readonly deps: CartSaverDeps,
    seed: { cartId?: string; ids: string[] }
  ) {
    this.cartId = seed.cartId;
    this.savedIds = seed.ids;
    this.latestIds = seed.ids;
  }

  update(ids: string[]) {
    this.latestIds = ids;
    if (!this.isDirty()) {
      this.clearDebounce();
      return;
    }
    if (!this.cartId) void this.drain();
    else this.debounce();
  }

  /**
   * Before a full page load: waits up to `maxWaitMs` for the request in flight and a JWT, then
   * starts the last save with keepalive without waiting for it. Resolves to the cart id to use.
   */
  async flushBeforeUnload(maxWaitMs: number): Promise<string | undefined> {
    this.clearDebounce();
    let capTimer: ReturnType<typeof setTimeout> | undefined;
    const cap = new Promise<void>(resolve => {
      capTimer = setTimeout(resolve, maxWaitMs);
    });
    try {
      if (this.inFlight) await Promise.race([this.inFlight, cap]);
      // The request we waited for may have scheduled a debounced follow-up.
      this.clearDebounce();
      if (!this.isDirty()) return this.cartId;
      const jwt = await Promise.race([this.fetchJwt(), cap.then(() => this.lastJwt)]);
      // Tracked, so if the page survives (a blocked navigation, or bfcache), later saves queue behind it.
      void this.track(this.sendWithRetry(true, jwt));
      return this.cartId;
    } finally {
      clearTimeout(capTimer);
    }
  }

  /** On unmount: send any pending change now, after the request in flight. */
  drainNow() {
    this.clearDebounce();
    if (this.inFlight) this.drainAfterInFlight = true;
    else void this.drain();
  }

  private isDirty() {
    // A cart that was never created and is still empty has nothing to save.
    return !sameIds(this.latestIds, this.savedIds) && (!!this.cartId || this.latestIds.length > 0);
  }

  private debounce() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.drain();
    }, SAVE_DEBOUNCE_MS);
  }

  private clearDebounce() {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  private drain(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (!this.isDirty()) return Promise.resolve();
    return this.track(this.sendPending());
  }

  /**
   * Marks a request in flight until it and any request already in flight finish (a flush can
   * overlap a drain when Donate's cap runs out). The last to finish schedules what follows.
   */
  private track(request: Promise<SaveOutcome>): Promise<void> {
    const previous = this.inFlight;
    const settle = async () => {
      let outcome: SaveOutcome = 'failed';
      try {
        [, outcome] = await Promise.all([previous, request]);
      } finally {
        if (this.inFlight === tracked) this.inFlight = undefined;
      }
      if (!this.inFlight) this.afterDrain(outcome);
    };
    const tracked = settle();
    this.inFlight = tracked;
    return tracked;
  }

  private afterDrain(outcome: SaveOutcome) {
    const drainNow = this.drainAfterInFlight;
    this.drainAfterInFlight = false;
    // A failure waits for the next change; resending the same request would fail the same way.
    if (outcome !== 'saved' || !this.isDirty()) return;
    if (drainNow) void this.drain();
    else if (!this.timer) this.debounce();
  }

  private async sendPending(): Promise<SaveOutcome> {
    const wasCreate = !this.cartId;
    const outcome = await this.sendWithRetry(false, await this.fetchJwt());
    // Changes made during the create go out now, in one PATCH.
    if (outcome === 'saved' && wasCreate && this.isDirty()) {
      return this.sendWithRetry(false, await this.fetchJwt());
    }
    return outcome;
  }

  private async fetchJwt() {
    this.lastJwt = await this.deps.getJwt();
    return this.lastJwt;
  }

  private async sendWithRetry(keepalive: boolean, jwt: string | undefined) {
    const outcome = await this.send(keepalive, jwt);
    // Closed or gone: re-POST once so the change isn't lost (it upserts onto the donor's open cart).
    return outcome === 'retry' ? this.send(keepalive, jwt) : outcome;
  }

  private async send(keepalive: boolean, jwt: string | undefined): Promise<SaveOutcome> {
    const { clientId, refCode, createCart, updateCart, onSaved } = this.deps;
    const id = this.cartId;
    const ids = this.latestIds;
    try {
      const cart = id
        ? await updateCart({ id, recipient_ids: ids, jwt, keepalive }).unwrap()
        : // keepalive: Donate right after a first add still saves it.
          await createCart({
            client_id: clientId,
            recipient_ids: ids,
            ref_code: refCode,
            jwt,
            keepalive: true,
          }).unwrap();
      this.cartId = cart.id;
      this.savedIds = ids;
      onSaved(cart, ids);
      return 'saved';
    } catch (error) {
      this.logFailure(id ? 'update' : 'create', error);
      const status = errorStatus(error);
      if (id && (status === 409 || status === 404)) {
        this.cartId = undefined;
        return 'retry';
      }
      // 401, 422 and the rest: keep the id; the next change saves the whole selection again.
      return 'failed';
    }
  }

  private logFailure(operation: 'create' | 'update', error: unknown) {
    const status = errorStatus(error);
    // 401 (login expired), 404 and 409 (cart gone or closed) are expected.
    const isExpected = status === 401 || status === 404 || status === 409;
    const level = isExpected ? 'info' : isTransientFailure(error) ? 'warn' : 'error';
    datadogLogs.logger[level]('Saved cart save failed', {
      client_id: this.deps.clientId,
      operation,
      http_status: status,
      recipient_count: this.latestIds.length,
    });
  }
}
